import type { CSSProperties, ReactNode } from "react";

export type RiskLevel =
  | "Negligible"
  | "Low"
  | "Moderate"
  | "Medium"
  | "High"
  | "Extreme"
  | "Highest";

type Size = "sm" | "md" | "lg";

const RISK_MAP: Record<RiskLevel, { bg: string; fg: string; score: number }> = {
  Negligible: { bg: "var(--r-neg)", fg: "var(--r-neg-ink)", score: 1 },
  Low: { bg: "var(--r-low)", fg: "var(--r-low-ink)", score: 2 },
  Moderate: { bg: "var(--r-mod)", fg: "var(--r-mod-ink)", score: 3 },
  Medium: { bg: "var(--r-mod)", fg: "var(--r-mod-ink)", score: 3 },
  High: { bg: "var(--r-high)", fg: "var(--r-high-ink)", score: 4 },
  Extreme: { bg: "var(--r-ext)", fg: "var(--r-ext-ink)", score: 5 },
  Highest: { bg: "var(--r-ext)", fg: "var(--r-ext-ink)", score: 5 },
};

const SIZE: Record<Size, { fs: string; py: string; px: string }> = {
  sm: { fs: "text-[10px]", py: "py-[1px]", px: "px-1.5" },
  md: { fs: "text-[11px]", py: "py-[2px]", px: "px-2" },
  lg: { fs: "text-[12px]", py: "py-[3px]", px: "px-2.5" },
};

export interface RiskBadgeProps {
  level: RiskLevel;
  size?: Size;
  className?: string;
  style?: CSSProperties;
}

export function RiskBadge({
  level,
  size = "md",
  className = "",
  style,
}: RiskBadgeProps): ReactNode {
  const r = RISK_MAP[level] ?? RISK_MAP.Negligible;
  const s = SIZE[size];
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-1 border font-semibold tracking-[-0.05px] ${s.fs} ${s.py} ${s.px} ${className}`}
      style={{
        background: r.bg,
        color: r.fg,
        borderColor: `${r.fg}22`,
        ...style,
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: r.fg,
        }}
      />
      {level}
    </span>
  );
}

export function riskFromScore(score: number): RiskLevel {
  if (score >= 20) return "Extreme";
  if (score >= 12) return "High";
  if (score >= 6) return "Moderate";
  if (score >= 3) return "Low";
  return "Negligible";
}
